import {useEffect, useRef, useState} from "react";

/**
 * Tracks whether the referenced element is in the viewport, so animations can
 * pause while off-screen (restores the design's `.anim-live` behavior and keeps
 * heavy loops from burning frames when not visible).
 *
 * Starts `false` so off-screen content never animates before the observer runs;
 * on-screen content turns on within a frame. Falls back to `true` (always
 * animate) where IntersectionObserver is unavailable.
 */
export const useInView = <T extends Element = HTMLDivElement>(options?: IntersectionObserverInit) => {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), {
      threshold: 0,
      ...options,
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, inView] as const;
};
